/**
 * Checks if the current date is within the given range (in UTC).
 * @param fromDate - The start date of the range in UTC.
 * @param toDate - The end date of the range in UTC.
 * @param currentDate - The current date to compare (in UTC).
 * @returns {boolean} - True if the current date is within the range.
 */
export const isCurrentDateWithinRange = (
  fromDate: Date,
  toDate: Date,
  currentDate: Date
): boolean => {
  // Convert currentDate to UTC and strip the time part
  const currentDateUTC = new Date(
    Date.UTC(
      currentDate.getUTCFullYear(),
      currentDate.getUTCMonth(),
      currentDate.getUTCDate()
    )
  );

  return fromDate <= currentDateUTC && currentDateUTC <= toDate;
};

/**
 * Formats a UTC date string as a human-readable date in UTC timezone.
 *
 * @param utcDateString - A UTC date string in the format "YYYY-MM-DD" (e.g., "2024-09-27")
 * @returns A formatted date string in the format "Weekday, Month DD, YYYY" (e.g., "Friday, September 27, 2024")
 *
 * @example
 * ```ts
 * formatDateAsUTC("2024-09-27")
 * // Returns: "Friday, September 27, 2024"
 * ```
 */
export const formatDateAsUTC = (utcDateString: string) => {
  return new Date(utcDateString)
    .toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      weekday: 'long',
      timeZone: 'UTC',
    })
    .toString();
};
